import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./arap.css";
import { CheckCircle2, XCircle, RefreshCcw, Search, Plus } from "lucide-react";

function money(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

export default function AllocationWorkspace({ ledger = "AR" }) {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [me, setMe] = useState({ userId: null, role: "is_member" });

  const [q, setQ] = useState("");
  const [counterpartyId] = useState("");

  const [openItems, setOpenItems] = useState([]);
  const [credits, setCredits] = useState([]);

  const [selectedCredit, setSelectedCredit] = useState(null);
  const [allocRows, setAllocRows] = useState([]); // {target, amount}

  const isAR = String(ledger).toUpperCase() === "AR";

  const views = useMemo(() => {
    return {
      open: isAR ? "v_ar_open_items" : "v_ap_open_items",
      credits: isAR ? "v_ar_unapplied_credits" : "v_ap_unapplied_credits",
      counterpartyTable: isAR ? "customers" : "vendors",
      counterpartyNameField: isAR ? "company_name" : "name",
      counterpartyIdField: isAR ? "customer_id" : "vendor_id",
    };
  }, [isAR]);

  // ───────────────────────── Load my role ─────────────────────────
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      const userId = data?.user?.id || null;
      if (!userId || !companyId) return;

      const { data: cm } = await supabase
        .from("company_members")
        .select("role")
        .eq("company_id", companyId)
        .eq("user_id", userId)
        .maybeSingle();

      setMe({ userId, role: cm?.role || "is_member" });
    })();
  }, [companyId]);

  const isAdmin = me.role === "is_admin";

  // ───────────────────────── Fetch data ─────────────────────────
  async function reload() {
    setErr("");
    if (!companyId) return;
    setBusy(true);
    try {
      // open items
      const { data: oi, error: oiErr } = await supabase
        .from(views.open)
        .select("*")
        .eq("company_id", companyId)
        .order("due_date", { ascending: true })
        .limit(500);

      if (oiErr) throw oiErr;

      // credits
      const { data: cr, error: crErr } = await supabase
        .from(views.credits)
        .select("*")
        .eq("company_id", companyId)
        .order("posting_date", { ascending: true })
        .limit(500);

      if (crErr) throw crErr;

      setOpenItems(oi || []);
      setCredits(cr || []);
    } catch (e) {
      setErr(e?.message || "Failed to load allocation workspace.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, ledger]);

  // ───────────────────────── Derived lists ─────────────────────────
  const filteredCredits = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (credits || []).filter((c) => {
      if (counterpartyId && c[views.counterpartyIdField] !== counterpartyId)
        return false;

      if (!s) return true;
      return (
        String(c.source_type || "")
          .toLowerCase()
          .includes(s) ||
        String(c.source_id || "")
          .toLowerCase()
          .includes(s) ||
        String(c.anchor_journal_entry_id || "")
          .toLowerCase()
          .includes(s)
      );
    });
  }, [credits, q, counterpartyId, views]);

  const filteredOpenItems = useMemo(() => {
    if (!selectedCredit) return [];
    const cpId = selectedCredit[views.counterpartyIdField];
    return (openItems || []).filter(
      (o) => o[views.counterpartyIdField] === cpId
    );
  }, [openItems, selectedCredit, views]);

  const creditRemaining = useMemo(() => {
    if (!selectedCredit) return 0;
    const applied = allocRows.reduce((s, r) => s + Number(r.amount || 0), 0);
    return Math.max(Number(selectedCredit.unapplied_amount || 0) - applied, 0);
  }, [selectedCredit, allocRows]);

  // ───────────────────────── UX actions ─────────────────────────
  function chooseCredit(c) {
    setSelectedCredit(c);
    setAllocRows([]);
  }

  function addAlloc(target) {
    if (!selectedCredit) return;

    // default allocate min(open_amount, remaining)
    const remaining = creditRemaining;
    const open = Number(target.open_amount || 0);
    const amount = Math.max(Math.min(open, remaining), 0);

    if (!(amount > 0)) return;

    setAllocRows((p) => [
      ...p,
      {
        target,
        amount,
        key: `${target.anchor_journal_entry_id}-${Date.now()}`,
      },
    ]);
  }

  function updateAllocAmount(key, amount) {
    setAllocRows((p) => p.map((r) => (r.key === key ? { ...r, amount } : r)));
  }

  function removeAlloc(key) {
    setAllocRows((p) => p.filter((r) => r.key !== key));
  }

  async function submitAllocations() {
    setErr("");
    if (!companyId) return;
    if (!selectedCredit) return setErr("Pick a payment/credit first.");
    if (!allocRows.length) return setErr("Add at least one allocation line.");

    const cpType = isAR ? "customer" : "vendor";
    const cpId = selectedCredit[views.counterpartyIdField];

    // validations (client side)
    const totalAlloc = allocRows.reduce((s, r) => s + Number(r.amount || 0), 0);
    if (!(totalAlloc > 0)) return setErr("Allocation total must be > 0.");
    if (totalAlloc > Number(selectedCredit.unapplied_amount || 0))
      return setErr("Cannot allocate more than unapplied amount.");

    // per line open checks
    for (const r of allocRows) {
      if (Number(r.amount || 0) > Number(r.target.open_amount || 0)) {
        return setErr("Cannot allocate beyond an open item’s open amount.");
      }
    }

    setBusy(true);
    try {
      // Create allocations via RPC (member => PENDING + approval; admin => ACTIVE immediately)
      for (const r of allocRows) {
        const { error } = await supabase.rpc("rpc_create_allocation", {
          p_company_id: companyId,
          p_ledger: isAR ? "AR" : "AP",
          p_counterparty_type: cpType,
          p_counterparty_id: cpId,
          p_source_journal_entry_id: selectedCredit.anchor_journal_entry_id,
          p_target_journal_entry_id: r.target.anchor_journal_entry_id,
          p_amount: Number(r.amount || 0),
          p_note: `Allocation ${isAR ? "AR" : "AP"} via workspace`,
        });

        if (error) throw error;
      }

      alert(
        isAdmin
          ? "Allocation posted (ACTIVE)."
          : "Allocation submitted for approval (PENDING)."
      );

      setSelectedCredit(null);
      setAllocRows([]);
      await reload();
    } catch (e) {
      setErr(e?.message || "Failed to submit allocations.");
    } finally {
      setBusy(false);
    }
  }

  // ───────────────────────── Render ─────────────────────────
  return (
    <div className="arap-page">
      <header className="arap-head panel">
        <div>
          <div className="arap-title">
            {isAR ? "AR Allocation Workspace" : "AP Allocation Workspace"}
          </div>
          <div className="arap-sub">
            Journal-driven. Allocations only map settlement; balances remain
            derived from journal.
          </div>
        </div>

        <div className="arap-actions">
          <div className="arap-search">
            <Search size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by source_type, source_id, journal id…"
              disabled={busy}
            />
          </div>

          <button className="btn-ghost" onClick={reload} disabled={busy}>
            <RefreshCcw size={16} /> Refresh
          </button>
        </div>
      </header>

      {err && <div className="banner error panel">{err}</div>}

      <div className="arap-grid">
        {/* LEFT: credits */}
        <section className="panel arap-card">
          <div className="arap-card-head">
            <h3>
              {isAR ? "Unapplied Customer Credits" : "Unapplied Vendor Credits"}
            </h3>
            <span className="chip">{filteredCredits.length}</span>
          </div>

          <div className="arap-table-wrap">
            <table className="arap-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th className="num">Unapplied</th>
                  <th className="mono">Anchor JE</th>
                </tr>
              </thead>
              <tbody>
                {filteredCredits.map((c) => {
                  const active =
                    selectedCredit?.anchor_journal_entry_id ===
                    c.anchor_journal_entry_id;
                  return (
                    <tr
                      key={c.anchor_journal_entry_id}
                      className={active ? "active" : ""}
                      onClick={() => chooseCredit(c)}
                      role="button"
                      tabIndex={0}
                    >
                      <td>
                        {c.posting_date
                          ? new Date(c.posting_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <div className="cell-main">
                          <strong>
                            {String(c.source_type || "—").toUpperCase()}
                          </strong>
                          <div className="muted small mono">
                            {c.source_id || "—"}
                          </div>
                        </div>
                      </td>
                      <td className="num">{money(c.unapplied_amount)}</td>
                      <td className="mono">
                        {String(c.anchor_journal_entry_id || "")
                          .slice(0, 8)
                          .toUpperCase()}
                      </td>
                    </tr>
                  );
                })}

                {!filteredCredits.length && (
                  <tr>
                    <td colSpan={4}>
                      <div className="empty-state">
                        No unapplied credits found. Make sure your
                        payment/credit journals hit AR/AP control accounts and
                        include counterparty_id.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* RIGHT: open items + allocation cart */}
        <section className="panel arap-card">
          <div className="arap-card-head">
            <h3>{isAR ? "Open Invoices" : "Open Bills"}</h3>

            {selectedCredit ? (
              <div className="pill">
                Remaining: <strong>{money(creditRemaining)}</strong>
              </div>
            ) : (
              <div className="muted small">Select a credit to allocate</div>
            )}
          </div>

          {!selectedCredit ? (
            <div className="empty-state">
              Select an unapplied credit on the left to view open items.
            </div>
          ) : (
            <>
              <div className="arap-table-wrap">
                <table className="arap-table">
                  <thead>
                    <tr>
                      <th>Due</th>
                      <th>Source</th>
                      <th className="num">Open</th>
                      <th className="center">Add</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOpenItems.map((o) => (
                      <tr key={o.anchor_journal_entry_id}>
                        <td>
                          {o.due_date
                            ? new Date(o.due_date).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <div className="cell-main">
                            <strong>
                              {String(o.source_type || "—").toUpperCase()}
                            </strong>
                            <div className="muted small mono">
                              {o.source_id || "—"}
                            </div>
                          </div>
                        </td>
                        <td className="num">{money(o.open_amount)}</td>
                        <td className="center">
                          <button
                            className="icon-btn"
                            onClick={() => addAlloc(o)}
                            disabled={busy || creditRemaining <= 0}
                            title="Add allocation"
                          >
                            <Plus size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {!filteredOpenItems.length && (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state">
                            No open items found for this counterparty.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Allocation cart */}
              <div className="arap-cart">
                <div className="arap-cart-head">
                  <h4>Allocation Cart</h4>
                  <div className="muted small">
                    {isAdmin ? "Admin posts ACTIVE" : "Member submits PENDING"}
                  </div>
                </div>

                {allocRows.length === 0 ? (
                  <div className="empty-state">
                    Add one or more open items to allocate.
                  </div>
                ) : (
                  <div className="arap-cart-list">
                    {allocRows.map((r) => (
                      <div key={r.key} className="arap-cart-row">
                        <div className="cart-left">
                          <div className="mono small">
                            {String(r.target.source_type || "").toUpperCase()} •{" "}
                            {String(r.target.source_id || "").slice(0, 8)}
                          </div>
                          <div className="muted small">
                            Open: {money(r.target.open_amount)}
                          </div>
                        </div>

                        <div className="cart-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={r.amount}
                            onChange={(e) =>
                              updateAllocAmount(
                                r.key,
                                Number(e.target.value || 0)
                              )
                            }
                            disabled={busy}
                          />
                          <button
                            className="icon-btn danger"
                            onClick={() => removeAlloc(r.key)}
                            disabled={busy}
                            title="Remove"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="arap-cart-foot">
                  <button
                    className="btn-primary"
                    onClick={submitAllocations}
                    disabled={busy || !allocRows.length}
                  >
                    <CheckCircle2 size={16} /> Submit
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
